import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import config from '../config';

// Get API URL from environment or use relative URL in production
const getApiUrl = () => {
  // In production, if VITE_API_URL is not set, use relative URL
  if (import.meta.env.PROD && !import.meta.env.VITE_API_URL) {
    return ''; // Use relative URLs in production
  }
  return config.apiUrl;
};

// Create axios instance with base configuration
const axiosInstance = axios.create({
  baseURL: getApiUrl(),
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Helper to clear auth and redirect
const clearAuthAndRedirect = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userDetails');
  localStorage.removeItem('selectedBrand');
  // Only redirect if not already on login page
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

// Try to refresh the token using Clerk
const tryRefreshToken = async () => {
  try {
    const clerk = window.Clerk;

    if (!clerk || !clerk.session) {
      throw new Error('No Clerk session');
    }

    // Get a fresh token from Clerk
    const clerkToken = await clerk.session.getToken();
    if (!clerkToken) {
      throw new Error('Could not get Clerk token');
    }

    // Exchange Clerk token for backend token
    const response = await axios.post(`${getApiUrl()}/api/auth/clerk/`, {
      clerk_token: clerkToken,
      email: clerk.user?.primaryEmailAddress?.emailAddress,
      clerk_user_id: clerk.user?.id,
      first_name: clerk.user?.firstName,
      last_name: clerk.user?.lastName,
      avatar_url: clerk.user?.imageUrl,
    });

    if (response.data.allowed && response.data.access) {
      localStorage.setItem('accessToken', response.data.access);
      if (response.data.refresh) {
        localStorage.setItem('refreshToken', response.data.refresh);
      }
      if (response.data.user) {
        localStorage.setItem('userDetails', JSON.stringify(response.data.user));
      }
      return response.data.access;
    }

    throw new Error('Token refresh failed');
  } catch (error) {
    console.error('Token refresh failed:', error);
    throw error;
  }
};

// Request interceptor to handle JWT token authentication
axiosInstance.interceptors.request.use(
  async (requestConfig) => {
    const token = localStorage.getItem('accessToken');

    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const isTokenExpired = decodedToken.exp * 1000 < Date.now();
        // Check if token will expire in next 60 seconds
        const isTokenExpiringSoon = decodedToken.exp * 1000 < Date.now() + 60000;

        if (isTokenExpired || isTokenExpiringSoon) {
          // Try to refresh the token silently
          if (!isRefreshing) {
            isRefreshing = true;
            try {
              const newToken = await tryRefreshToken();
              isRefreshing = false;
              processQueue(null, newToken);
              requestConfig.headers.Authorization = `Bearer ${newToken}`;
              return requestConfig;
            } catch (refreshError) {
              isRefreshing = false;
              processQueue(refreshError, null);
              clearAuthAndRedirect();
              return Promise.reject(new Error('Token expired'));
            }
          } else {
            // Wait for the refresh to complete
            return new Promise((resolve, reject) => {
              failedQueue.push({
                resolve: (newToken) => {
                  requestConfig.headers.Authorization = `Bearer ${newToken}`;
                  resolve(requestConfig);
                },
                reject: (err) => {
                  reject(err);
                }
              });
            });
          }
        }

        // Add Bearer token to request headers
        requestConfig.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        // Token decode error - clear and redirect
        clearAuthAndRedirect();
        return Promise.reject(error);
      }
    }

    return requestConfig;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle 401 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await tryRefreshToken();
          isRefreshing = false;
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError, null);
          clearAuthAndRedirect();
          return Promise.reject(refreshError);
        }
      } else {
        // Wait for refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(axiosInstance(originalRequest));
            },
            reject: (err) => {
              reject(err);
            }
          });
        });
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
